import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getV2PortfolioDetail, rebuildV2Portfolio } from '../../composables/fetchAPI';
import { usePortfolio } from '../../context/PortfolioContext';

const statusLabel: Record<string, string> = {
  REBUILD_REQUIRED: 'ต้องทำการ Rebuild',
  REBUILD_FAILED: 'Rebuild ล้มเหลวครั้งก่อน',
  ERROR: 'พบข้อผิดพลาด',
  OK: 'ปกติ',
  IN_PROGRESS: 'กำลังประมวลผล',
};

const statusColor: Record<string, string> = {
  REBUILD_REQUIRED: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/40',
  REBUILD_FAILED: 'text-red-400 bg-red-500/10 border-red-500/40',
  ERROR: 'text-red-400 bg-red-500/10 border-red-500/40',
  OK: 'text-green-400 bg-green-500/10 border-green-500/40',
  IN_PROGRESS: 'text-blue-400 bg-blue-500/10 border-blue-500/40',
};

const PortfolioRebuildPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshUserPortfolios } = usePortfolio();

  const [portfolioData, setPortfolioData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRebuilding, setIsRebuilding] = useState(false);

  const fetchDetail = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const res = await getV2PortfolioDetail(id);
      const portData = res?.data?.portfolio || res?.portfolio || res?.data || res;
      setPortfolioData(portData);
    } catch (error: any) {
      console.error('Fetch portfolio detail error:', error);
      toast.error(error?.th_message || error?.message || 'ไม่สามารถโหลดข้อมูลพอร์ตได้');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRebuild = async () => {
    if (!id) return;
    const confirmed = window.confirm(
      'ต้องการซ่อมพอร์ต (Rebuild Snapshot) ใช่หรือไม่?\n\nระบบจะคำนวณสถานะพอร์ตใหม่ทั้งหมดจากประวัติรายการ (Transaction Ledger)'
    );
    if (!confirmed) return;

    setIsRebuilding(true);
    try {
      await rebuildV2Portfolio(id);
      toast.success('ส่งคำสั่ง Rebuild สำเร็จ');
      await refreshUserPortfolios();
      navigate(`/portfolio/${id}`);
    } catch (error: any) {
      console.error('Rebuild portfolio error:', error);
      toast.error(error?.th_message || error?.message || 'ไม่สามารถ Rebuild ได้');
    } finally {
      setIsRebuilding(false);
    }
  };

  if (isLoading) {
    return <div className="text-center mt-20 text-gray-400">กำลังโหลดข้อมูลพอร์ต...</div>;
  }

  if (!portfolioData) {
    return (
      <div className="text-center mt-20 text-gray-400">
        ไม่พบข้อมูลพอร์ต
        <div className="mt-4">
          <button
            onClick={() => navigate('/portfolio')}
            className="px-4 py-2 rounded hover:bg-gray-600 transition-colors"
            style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
          >
            &larr; กลับไปหน้าพอร์ต
          </button>
        </div>
      </div>
    );
  }

  const snapshotState = portfolioData.snapshotState || {};
  const status = snapshotState.status;
  const affectedTransactions = snapshotState.affectedTransactions || [];

  return (
    <div className="p-4 max-w-3xl mx-auto animate-fadeIn">
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/portfolio/${id}`)}
          className="px-4 py-2 rounded hover:bg-gray-600 transition-colors whitespace-nowrap"
          style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
        >
          &larr; <span className="hidden sm:inline">ย้อนกลับ</span>
          <span className="sm:hidden">กลับ</span>
        </button>
        <h1 className="text-xl md:text-2xl font-bold truncate" style={{ color: 'var(--text-color)' }}>
          ซ่อมพอร์ต (Rebuild)
        </h1>
      </div>

      <div className="rounded-xl shadow-lg p-5 space-y-4" style={{ backgroundColor: 'var(--component-bg-color)' }}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-700 pb-4">
          <div>
            <div className="text-xs" style={{ color: 'var(--secondary-text-color)' }}>ชื่อพอร์ต</div>
            <div className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>{portfolioData.name || '-'}</div>
          </div>
          <div>
            <div className="text-xs" style={{ color: 'var(--secondary-text-color)' }}>ID พอร์ต</div>
            <div className="text-sm font-mono select-all" style={{ color: 'var(--text-color)' }}>{id}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--secondary-text-color)' }}>สถานะ:</span>
          <span
            className={`text-xs font-bold px-2.5 py-1 rounded-full border ${statusColor[status] || 'text-gray-300 bg-gray-500/10 border-gray-500/40'}`}
          >
            {statusLabel[status] || status || 'ไม่ทราบสถานะ'}
          </span>
          {snapshotState.reconciliationRequired && (
            <span className="text-xs font-bold px-2.5 py-1 rounded-full border text-yellow-400 bg-yellow-500/10 border-yellow-500/40">
              ต้องทำ Reconciliation
            </span>
          )}
        </div>

        {affectedTransactions.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-xs text-yellow-200/90 leading-relaxed">
            <span className="font-bold text-yellow-400">⚠️ พบรายการที่อาจได้รับผลกระทบ:</span> {affectedTransactions.length} รายการ
            <div className="text-[11px] mt-1" style={{ color: 'var(--secondary-text-color)' }}>
              แนะนำให้ตรวจสอบและแก้ไขรายการที่มีปัญหาก่อน Rebuild เพื่อผลลัพธ์ที่ถูกต้องที่สุด
            </div>
          </div>
        )}

        <div className="bg-black/20 border border-gray-700 rounded-xl p-3 text-xs leading-relaxed" style={{ color: 'var(--secondary-text-color)' }}>
          💡 การ Rebuild คือการสั่งให้ระบบคำนวณ Snapshot ของพอร์ตใหม่ทั้งหมดจากประวัติรายการ (Transaction Ledger) เพื่อแก้ไขข้อมูลมูลค่าพอร์ต/จำนวนหุ้นที่ไม่ตรงกัน
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={handleRebuild}
            disabled={isRebuilding}
            className="bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white font-bold px-6 py-3 rounded-lg shadow transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRebuilding ? 'กำลังประมวลผล...' : '⚠️ ซ่อมพอร์ต (Rebuild)'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PortfolioRebuildPage;
